import {useCallback, useEffect, useRef, useState} from "react";
import debounce from "../utils/debounce";

export default function useInfiniteScroll({ listGetter, rootRef, sentinelRef, initial=24, step=24 }) {
  const [visibleCount, setVisibleCount] = useState(initial);
  const [isPaging, setIsPaging] = useState(false);
  const [observerSupported, setObserverSupported] = useState(true);
  const [isScrollable, setIsScrollable] = useState(false);

  const getList = useCallback(() => listGetter() || [], [listGetter]);
  const hasMore = useCallback(() => visibleCount < getList().length, [visibleCount, getList]);

  const loadMore = useCallback(() => {
    if (isPaging || !hasMore()) return;
    setIsPaging(true);
    setTimeout(() => {
      setVisibleCount(n => Math.min(n + step, getList().length));
      setIsPaging(false);
    }, 200);
  }, [isPaging, hasMore, getList, step]);

  const resetVisible = useCallback(() => setVisibleCount(initial), [initial]);

  const recalcScrollable = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    setIsScrollable(el.scrollHeight > el.clientHeight + 2);
  }, [rootRef]);

  // observer & fallbacks
  useEffect(() => {
    setObserverSupported(typeof window !== "undefined" && "IntersectionObserver" in window);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    const sentinel = sentinelRef.current;
    if (!el || !sentinel) return;
    if (!observerSupported) return;
    if (!isScrollable) return;

    const ob = new IntersectionObserver(
      entries => { if (entries[0]?.isIntersecting) loadMore(); },
      { root: el, rootMargin: "0px 0px 200px 0px", threshold: 0.01 }
    );
    ob.observe(sentinel);
    return () => ob.disconnect();
  }, [observerSupported, isScrollable, rootRef, sentinelRef, loadMore, visibleCount]);

  useEffect(() => {
    if (observerSupported) return;
    const el = rootRef.current;
    if (!el) return;
    const nearBottom = () => el.scrollTop + el.clientHeight >= el.scrollHeight - 200;
    const onScroll = debounce(() => { if (nearBottom()) loadMore(); }, 150);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [observerSupported, rootRef, loadMore]);

  useEffect(() => {
    recalcScrollable();
    const onResize = debounce(recalcScrollable, 150);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [recalcScrollable, visibleCount]);

  return { visibleCount, isPaging, hasMore, loadMore, resetVisible, isScrollable, recalcScrollable, observerSupported };
}